import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Table, Btn, Badge } from "@/components/UI";
import { usePagination, ListPagination } from "@/components/ListPagination";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { CustomerFormModal } from "@/components/customers/CustomerFormModal";
import { CustomerFilters } from "@/components/customers/CustomerFilters";
import { CustomerTypeBadge } from "@/components/customers/CustomerTypeBadge";
import { apiError } from "@/lib/customers";

const trLower = (s) => (s || "").toLocaleLowerCase("tr");

function applyFilters(rows, { q, type, status }) {
  const ql = trLower(q.trim());
  return rows.filter((r) =>
    (!type || r.customer_type === type) &&
    (!status || (status === "active" ? r.active : !r.active)) &&
    (!ql || [r.legal_name, r.code, r.contact_name, r.phone, r.email, r.telegram_username].some((v) => trLower(v).includes(ql))));
}

export default function CustomersPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: "", type: "", status: "" });
  const [modal, setModal] = useState({ open: false, customer: null });
  const load = () => api.get("/customers").then(setRows);
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => applyFilters(rows, filters), [rows, filters]);
  const pg = usePagination(filtered, "customers", 100);

  const remove = async (r) => {
    if (!window.confirm(`${r.legal_name} müşteri kartı silinsin mi?\n\nKayıt 30 gün boyunca Çöp Kutusu'ndan geri yüklenebilir.`)) return;
    try { await api.del(`/customers/${r.id}`); load(); } catch (e) { alert(apiError(e, "Silme başarısız")); }
  };
  const actions = {
    label: "İşlem",
    render: (r) => (
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        <Btn size="sm" variant="secondary" onClick={() => setModal({ open: true, customer: r })} data-testid={`edit-cust-${r.id}`}><Pencil size={12} /> Düzenle</Btn>
        {user?.role === "admin" && <Btn size="sm" variant="danger" onClick={() => remove(r)} data-testid={`delete-cust-${r.id}`}><Trash2 size={12} /> Sil</Btn>}
      </div>
    ),
  };

  return (
    <div data-testid="customers-page">
      <PageHeader title="Müşteriler" description={`Müşteri kartları — ${rows.length} kayıt`}
        actions={<Btn onClick={() => setModal({ open: true, customer: null })} data-testid="new-customer-btn"><Plus size={14} /> Yeni Müşteri</Btn>} />
      <CustomerFilters rows={rows} filters={filters} onChange={setFilters} />
      <Table testid="customers-table" onRowClick={(r) => nav(`/customers/${r.id}`)}
        empty={rows.length ? "Filtreye uyan müşteri yok" : "Henüz müşteri kartı yok — “Yeni Müşteri” ile ekleyin"}
        columns={[
          { label: "Firma", render: (r) => <span className="font-medium" data-testid={`cust-row-name-${r.id}`}>{r.legal_name}</span> },
          { label: "Kod", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
          { label: "Tip", render: (r) => <CustomerTypeBadge type={r.customer_type} testid={`cust-row-type-${r.id}`} /> },
          { label: "Yetkili", key: "contact_name" },
          { label: "Telefon", key: "phone" },
          { label: "E-posta", key: "email" },
          { label: "Vade", render: (r) => `${r.default_payment_days || 0} gün` },
          { label: "Durum", render: (r) => <Badge level={r.active ? "ok" : "inactive"}>{r.active ? "Aktif" : "Pasif"}</Badge> },
          actions,
        ]}
        rows={pg.paged} />
      <ListPagination pagination={pg} testid="customers-pagination" />
      <CustomerFormModal open={modal.open} customer={modal.customer}
        onClose={() => setModal({ open: false, customer: null })}
        onSaved={() => { setModal({ open: false, customer: null }); load(); }} />
    </div>
  );
}
